import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  DxAutocompleteModule,
  DxDateBoxModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTextBoxModule,
} from "devextreme-angular";
import { SelectionChangedEvent } from "devextreme/ui/autocomplete";
import { CalendarDatePipe } from "../../../../../core/utils/calendars-date.pipe";
import { DateFormatter } from "../../../../../core/utils/date-formatter";
import { showNotification } from "../../../../../core/utils/toast";
import { CreateWorkloadDto } from "../../../../../data/repositories/agenda/entities/agenda.model";
import { Resource } from "../../../../../domain/models/personnel.model";
import { GetAllActivePEUseCase } from "../../../../../domain/use-cases/hr/get-all-active-pe.usecase";
import { AgendaService } from "../../services/agenda.service";

@Component({
  selector: "app-create-workload-modal",
  standalone: true,
  imports: [
    CommonModule,
    DxSelectBoxModule,
    DxPopupModule,
    DxDateBoxModule,
    DxSelectBoxModule,
    DxAutocompleteModule,
    DxTextBoxModule,
    ReactiveFormsModule,
    CalendarDatePipe,
  ],
  templateUrl: "./create-workload-modal.component.html",
  styleUrl: "./create-workload-modal.component.scss",
})
export class CreateWorkloadModalComponent implements OnInit {
  @Input() isPopupVisible: boolean = false;

  @Output() closeModalEmitter = new EventEmitter<number | null>();

  projectEngOptions: { id: number; name: string }[] = [];
  winProbOptions: number[] = new Array(10).fill(0).map((_, i) => (i + 1) * 10);
  clmOptions: string[] = ["CE", "VantagePoint", "CAI-SHIP"];
  today = new Date();
  todayPlusThreeMonths = new Date(
    new Date().setMonth(this.today.getMonth() + 3),
  );
  workloadForm!: FormGroup;
  closeButtonOptions;
  saveButtonOptions;
  chosenPE!: { id: number; name: string };
  errorsArray: { name: string; reason: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private agendaService: AgendaService,
    private getAllActivePEUseCase: GetAllActivePEUseCase,
  ) {
    this.closeButtonOptions = {
      text: "Close",
      stylingMode: "outlined",
      type: "normal",
      onClick: () => {
        this.closeModalEmitter.emit(null);
        this.resetForm();
      },
    };
    this.saveButtonOptions = {
      text: "Save",
      stylingMode: "contained",
      type: "default",
      onClick: () => this.submitForm(),
    };
  }

  ngOnInit(): void {
    this.getAllActivePEUseCase
      .execute({
        startDate: DateFormatter.toAPIString(this.today),
        endDate: DateFormatter.toAPIString(this.todayPlusThreeMonths),
      })
      .subscribe((data: Resource[]) => {
        let mappedResources = data.map((el) => ({
          id: el.id,
          name: el.name + " " + el.surname,
        }));

        this.projectEngOptions = mappedResources;
        this.chosenPE = mappedResources[0];
        this.workloadForm = this.fb.group(
          {
            projectEngineer: this.fb.control(
              this.projectEngOptions[0].name,
              Validators.required,
            ),
            customer: this.fb.control("", Validators.required),
            workloadName: this.fb.control("", Validators.required),
            startDate: this.fb.control(this.today, Validators.required),
            endDate: this.fb.control(
              this.todayPlusThreeMonths,
              Validators.required,
            ),
            winProbability: this.fb.control(null, Validators.required),
            clm: this.fb.control(this.clmOptions[0], Validators.required),
          },
          { updateOn: "change" },
        );
      });
  }

  onCloseModal(): void {
    this.closeModalEmitter.emit(null);
    this.resetForm();
  }

  submitForm() {
    let externalResource;
    switch (this.workloadForm.controls["clm"].value) {
      case "CE":
        externalResource = 0;
        break;
      case "VantagePoint":
        externalResource = 1;
        break;
      case "CAI-SHIP":
        externalResource = 2;
        break;
      default:
        break;
    }
    const createWorkloadDto: CreateWorkloadDto = {
      pepresourceId: this.chosenPE.id,
      name: this.workloadForm.controls["workloadName"].value!,
      customer: this.workloadForm.controls["customer"].value!,
      dateStart: this.workloadForm.controls["startDate"].value
        ?.toISOString()
        .split("T")[0]!,
      dateEnd: this.workloadForm.controls["endDate"].value
        ?.toISOString()
        .split("T")[0]!,
      winProbability: this.workloadForm.controls["winProbability"].value! / 10,
      externalResource: externalResource!,
    };
    this.errorsArray = [];
    this.agendaService.createWorkload(createWorkloadDto).subscribe({
      next: (response) => {
        showNotification("Workload created succesfully", "success");
        const newWorkloadId = response.createdAgreement.id;
        this.closeModalEmitter.emit(newWorkloadId);
      },
      error: (err) => {
        console.error(err);
        if (err.error.errors) {
          this.errorsArray = err.error.errors;
        }
        showNotification("Error while creating workload", "error");
      },
    });
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    if (event.selectedItem) {
      this.workloadForm.controls["projectEngineer"].setValue(
        event.selectedItem.id,
      );
      this.chosenPE = event.selectedItem;
    }
  }

  resetForm() {
    this.workloadForm.reset();
    this.workloadForm.patchValue({
      projectEngineer: this.projectEngOptions[0].name,
      startDate: this.today,
      endDate: this.todayPlusThreeMonths,
      clm: this.clmOptions[0],
    });
    this.chosenPE = this.projectEngOptions[0];
  }

  preparePE() {
    this.errorsArray = [];
    this.getAllActivePEUseCase
      .execute({
        startDate: DateFormatter.toAPIString(this.today),
        endDate: DateFormatter.toAPIString(this.todayPlusThreeMonths),
      })
      .subscribe((data: Resource[]) => {
        let mappedResources = data.map((el) => ({
          id: el.id,
          name: el.name + " " + el.surname,
        }));

        this.projectEngOptions = mappedResources;
        this.chosenPE = mappedResources[0];
      });
  }
}
